import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from '@/components';
import { Modal, ModalActionType, ModalVariant } from '@/components/Modal/Modal';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine/engine-service';
import { getErrorDetail } from '@/features/MergeIdentity/utils';
import type { VehicleMergeResult } from '@/types/vehicle';
import type { DuplicatePlateConflict } from '../utils/duplicatePlate';

interface DuplicatePlateMergeDialogProps {
    isOpen: boolean;
    sourceVehicleId: string;
    conflict: DuplicatePlateConflict;
    onCancel: () => void;
    onMerged: (result: VehicleMergeResult) => void;
    onSourceStale?: () => void;
}

export function DuplicatePlateMergeDialog({
    isOpen,
    sourceVehicleId,
    conflict,
    onCancel,
    onMerged,
    onSourceStale,
}: DuplicatePlateMergeDialogProps) {
    const { t } = useTranslation(I18nNamespace.DIRECTORY);
    const [isMerging, setIsMerging] = useState(false);

    const conflictName = conflict.name || t('mergeVehicleModal.unnamedVehicle');

    const handleMerge = async () => {
        setIsMerging(true);
        try {
            const result = await EngineService.mergeVehicles({
                sourceVehicleId,
                targetVehicleId: conflict.id,
            });
            toast.success(t('duplicatePlateDialog.success'));
            onMerged(result);
        } catch (error) {
            if (getErrorDetail(error) === 'SOURCE_NOT_FOUND') {
                toast.info(t('mergeVehicleModal.errorAlreadyMerged'));
                onSourceStale?.();
                return;
            }
            toast.error(t('duplicatePlateDialog.error'));
        } finally {
            setIsMerging(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onCancel}
            onCancel={onCancel}
            onSubmit={() => void handleMerge()}
            title={t('duplicatePlateDialog.title')}
            submitText={t('duplicatePlateDialog.merge')}
            submitInProgress={isMerging}
            actionType={ModalActionType.SECONDARY}
            variant={ModalVariant.PLAIN}
            size="small"
        >
            <div className="flex flex-col gap-3 px-6 py-4 text-sm text-grey-200">
                <p>
                    <Trans
                        i18nKey="duplicatePlateDialog.body"
                        ns={I18nNamespace.DIRECTORY}
                        values={{
                            plate: conflict.licensePlate,
                            name: conflictName,
                        }}
                        components={{
                            plate: (
                                <code className="rounded bg-grey-600 px-1 py-0.5 font-mono text-white-100" />
                            ),
                            name: (
                                <Tooltip delayDuration={0}>
                                    <TooltipTrigger asChild>
                                        <span className="inline-block max-w-[12rem] translate-y-1 truncate align-bottom font-medium text-white-100" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {conflictName}
                                    </TooltipContent>
                                </Tooltip>
                            ),
                        }}
                    />
                </p>
                <p>{t('duplicatePlateDialog.question')}</p>
            </div>
        </Modal>
    );
}
